import { useEffect, useId, useRef, useState } from "react";

export default function SearchableSelect({ value, onChange, options = [], placeholder = "Select an option", disabled = false, required = false, className = "", vehicleNumberSearch = false }) {
  const root = useRef(null);
  const input = useRef(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const selected = options.find((option) => String(option.value) === String(value));
  const [term, setTerm] = useState(selected?.label || "");

  useEffect(() => { setTerm(selected?.label || ""); }, [selected?.label]);
  useEffect(() => {
    const close = (event) => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  const vehicleKey = (text) => String(text || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const matchesOption = (option, search) => {
    const label = String(option.label || "");
    const normalizedSearch = String(search || "").trim();
    if (!normalizedSearch) return true;
    if (label.toLowerCase().includes(normalizedSearch.toLowerCase())) return true;
    if (!vehicleNumberSearch) return false;
    const labelKey = vehicleKey(label), searchKey = vehicleKey(normalizedSearch);
    return labelKey.includes(searchKey) || labelKey.replace(/0/g, "").includes(searchKey.replace(/0/g, ""));
  };
  const matches = options.filter((option) => matchesOption(option, term));
  const optionId = (index) => `${listId}-option-${index}`;
  const choose = (option) => {
    if (!option) return;
    onChange(option.value);
    input.current?.setCustomValidity("");
    setTerm(option.label);
    setOpen(false);
    setActiveIndex(-1);
  };
  const openWithActiveOption = (index = 0) => {
    setOpen(true);
    setActiveIndex(matches.length ? Math.max(0, Math.min(index, matches.length - 1)) : -1);
  };
  const updateSearch = (event) => {
    const nextTerm = event.target.value;
    const exactMatch = options.find((option) => {
      if (option.label.toLowerCase() === nextTerm.trim().toLowerCase()) return true;
      if (!vehicleNumberSearch) return false;
      const labelKey = vehicleKey(option.label), searchKey = vehicleKey(nextTerm);
      return labelKey === searchKey || labelKey.replace(/0/g, "") === searchKey.replace(/0/g, "");
    });
    const matchingOptions = options.filter((option) => matchesOption(option, nextTerm));
    const uniqueMatch = nextTerm.trim() && matchingOptions.length === 1 ? matchingOptions[0] : null;
    setTerm(nextTerm);
    if (exactMatch || uniqueMatch) onChange((exactMatch || uniqueMatch).value);
    event.target.setCustomValidity(required && nextTerm && !(exactMatch || uniqueMatch) ? "Choose an option from the list." : "");
    setOpen(true);
    setActiveIndex(0);
  };
  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openWithActiveOption(open ? activeIndex + 1 : 0);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openWithActiveOption(open ? activeIndex - 1 : matches.length - 1);
    } else if (event.key === "Home" && open) {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End" && open) {
      event.preventDefault();
      setActiveIndex(Math.max(0, matches.length - 1));
    } else if (event.key === "Enter" && open && activeIndex >= 0) {
      event.preventDefault();
      choose(matches[activeIndex]);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    } else if (event.key === "Tab") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return <div className={`searchable-select ${className}`} ref={root}>
    <input ref={input} value={term} required={required} disabled={disabled} placeholder={placeholder} autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={listId} aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined} onFocus={() => openWithActiveOption(0)} onChange={updateSearch} onKeyDown={handleKeyDown} onInvalid={(event) => event.currentTarget.setCustomValidity(required && !selected ? "Choose an option from the list." : "")} />
    <button type="button" aria-label={open ? "Hide options" : "Show options"} aria-expanded={open} disabled={disabled} onClick={() => open ? setOpen(false) : openWithActiveOption(0)}>⌄</button>
    {open && !disabled && <div id={listId} className="searchable-select-options" role="listbox">
      {matches.map((option, index) => <button type="button" key={option.value} id={optionId(index)} role="option" aria-selected={String(option.value) === String(value)} className={index === activeIndex ? "active" : ""} onMouseMove={() => setActiveIndex(index)} onClick={() => choose(option)}>{option.label}</button>)}
      {!matches.length && <span role="status">No matching options</span>}
    </div>}
  </div>;
}
